import type {
  ArtifactLine,
  ArtifactSurface,
  ExecutionArtifactSpec,
  ResolvedFigure,
  ShapeRole,
} from '../model.js';
import { box, finishFigure, point, SceneBuilder, validateViewport } from '../scene-builder.js';

const ARTIFACT_KINDS = new Set<ExecutionArtifactSpec['artifactKind']>(['terminal', 'explain', 'profile', 'source-diff']);
const SURFACES = new Set<ArtifactSurface>(['terminal', 'editor', 'query-inspector', 'profiler']);
const LINE_KINDS = new Set<ArtifactLine['kind']>(['command', 'output', 'evidence', 'muted']);

interface Frame { x: number; y: number; width: number; height: number; bottom: number; narrow: boolean }
interface RenderResult { startIndex: number; endIndex: number; horizontalCrop: boolean; subtitle: string }

export function executionArtifactLineId(lineId: string): string {
  return `artifact.line.${lineId}`;
}

export function executionArtifactSourceLineId(lineId: string): string {
  return `artifact.source.${lineId}`;
}

/**
 * Compiles immutable evidence into the grammar of the instrument displaying
 * it. A surface changes chrome and layout, never source strings or semantic
 * line identities. Text, baselines and tree/heat guides stay exact across
 * global hands; only Kiln's outer surface and selection treatment may vary.
 */
export function compileExecutionArtifact(spec: ExecutionArtifactSpec): ResolvedFigure {
  validateExecutionArtifact(spec);
  const builder = new SceneBuilder();
  const surface = resolveSurface(spec);
  const frame = resolveFrame(spec, surface);
  const warnings: string[] = [];

  addSourceRelations(builder, spec);
  addSurfaceFrame(builder, spec, surface, frame);
  const result = surface === 'terminal'
    ? renderTerminal(builder, spec, frame)
    : surface === 'editor'
      ? renderEditor(builder, spec, frame)
      : surface === 'query-inspector'
        ? renderQueryInspector(builder, spec, frame)
        : renderProfiler(builder, spec, frame);

  const croppedBefore = result.startIndex;
  const croppedAfter = spec.lines.length - result.endIndex;
  if (croppedBefore + croppedAfter > 0) warnings.push(`${croppedBefore + croppedAfter} verbatim source lines are outside the viewport window; no evidence was rewritten.`);
  if (result.horizontalCrop) warnings.push('Long artifact lines are cropped by the viewport; their source strings remain unchanged.');
  if (surface !== 'editor') addFooter(builder, spec, surface, frame, result, croppedBefore, croppedAfter);

  const active = spec.focusLineId ? [spec.focusLineId] : ['artifact'];
  active.push(...(spec.emphasis ?? []));
  return finishFigure(spec, builder, {
    title: spec.title,
    subtitle: result.subtitle,
    state: spec.focusLineId ? `focus:${spec.focusLineId}` : `surface:${surface}`,
    active,
    warnings,
  });
}

export function validateExecutionArtifact(spec: ExecutionArtifactSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (!ARTIFACT_KINDS.has(spec.artifactKind)) throw new Error(`execution-artifact: invalid artifact kind ${String(spec.artifactKind)}`);
  if (spec.surface && !SURFACES.has(spec.surface)) throw new Error(`execution-artifact: invalid artifact surface ${String(spec.surface)}`);
  if (!spec.title.trim() || spec.title.length > 100) throw new Error('execution-artifact: title must contain 1…100 characters');
  if (!spec.provenance.trim()) throw new Error('execution-artifact: provenance is required');
  if (spec.lines.length < 2 || spec.lines.length > 64) throw new Error('execution-artifact: require 2…64 source lines');

  const surface = resolveSurface(spec);
  if (surface === 'editor' && !spec.context?.fileName?.trim()) throw new Error('execution-artifact: editor surface requires context.fileName');
  if (surface === 'editor' && (!spec.source || spec.source.lines.length < 2)) throw new Error('execution-artifact: editor surface requires a multi-line source file');
  if (surface === 'profiler' && spec.artifactKind !== 'profile') throw new Error('execution-artifact: profiler surface requires a profile artifact');

  const ids = spec.lines.map((line) => line.id);
  if (new Set(ids).size !== ids.length) throw new Error('execution-artifact: line IDs must be unique');
  let evidenceCount = 0;
  let commandCount = 0;
  for (const line of spec.lines) {
    if (!/^[a-z][a-z0-9-]*$/.test(line.id)) throw new Error(`execution-artifact: invalid line id ${line.id}`);
    if (!LINE_KINDS.has(line.kind)) throw new Error(`execution-artifact: invalid line kind for ${line.id}`);
    if (!line.text.length) throw new Error(`execution-artifact: line ${line.id} cannot be empty`);
    if (line.text.length > 512) throw new Error(`execution-artifact: line ${line.id} exceeds 512 source characters`);
    if (/\r|\n|\t|[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(line.text)) throw new Error(`execution-artifact: line ${line.id} must be a single display line without control characters`);
    if (line.kind === 'evidence') evidenceCount += 1;
    if (line.kind === 'command') commandCount += 1;
  }
  if (evidenceCount === 0) throw new Error('execution-artifact: at least one line must be marked as evidence');
  if (spec.artifactKind !== 'source-diff' && commandCount === 0) throw new Error(`execution-artifact: ${spec.artifactKind} artifacts require a command line`);

  const semanticIds = new Set(ids.map(executionArtifactLineId));
  if (spec.focusLineId && !semanticIds.has(spec.focusLineId)) throw new Error(`execution-artifact: focus line ${spec.focusLineId} does not exist`);
  if (spec.source) {
    if (!spec.source.fileName.trim()) throw new Error('execution-artifact: source filename is required');
    const sourceIds = spec.source.lines.map((line) => line.id);
    if (new Set(sourceIds).size !== sourceIds.length) throw new Error('execution-artifact: source line IDs must be unique');
    for (const line of spec.source.lines) {
      if (!/^[a-z][a-z0-9-]*$/.test(line.id)) throw new Error(`execution-artifact: invalid source line id ${line.id}`);
      if (/\r|\n|\t|[\u0000-\u001f]/.test(line.text)) throw new Error(`execution-artifact: source line ${line.id} must be one display line`);
    }
    if (spec.source.activeLine !== undefined && (spec.source.activeLine < 1 || spec.source.activeLine > spec.source.lines.length)) throw new Error('execution-artifact: active source line is outside the file');
  }
}

function resolveFrame(spec: ExecutionArtifactSpec, surface: ArtifactSurface): Frame {
  const { width, height } = spec.viewport;
  const narrow = width <= 390;
  const margin = surface === 'editor' ? (width <= 340 ? 6 : 8) : width <= 340 ? 10 : Math.max(14, Math.min(28, width * 0.045));
  const y = surface === 'editor' ? 8 : narrow ? 42 : 44;
  const bottom = height - (surface === 'editor' ? 8 : 14);
  return { x: margin, y, width: width - margin * 2, height: bottom - y, bottom, narrow };
}

function resolveSurface(spec: ExecutionArtifactSpec): ArtifactSurface {
  if (spec.surface) return spec.surface;
  return spec.artifactKind === 'profile' ? 'profiler' : 'terminal';
}

function addSurfaceFrame(builder: SceneBuilder, spec: ExecutionArtifactSpec, surface: ArtifactSurface, frame: Frame): void {
  if (surface === 'editor') {
    builder.hit('artifact', box(frame.x, frame.y, frame.width, frame.height));
    return;
  }
  builder.rect('artifact.frame', 'artifact', 'artifact', box(frame.x, frame.y, frame.width, frame.height), { radius: frame.narrow ? 7 : 9, label: `${surface} evidence surface: ${spec.title}` });
  builder.text('artifact.surface-label', 'artifact', 'annotation', point(frame.x, 27), `${surface.replace('-', ' ')} · captured evidence`, { size: 9, mono: true, weight: 700, uppercase: true });
  builder.hit('artifact', box(frame.x, frame.y, frame.width, frame.height));
}

function renderTerminal(builder: SceneBuilder, spec: ExecutionArtifactSpec, frame: Frame): RenderResult {
  const chromeHeight = frame.narrow ? 38 : 40;
  const footerHeight = 36;
  const lineHeight = frame.narrow ? 20 : 22;
  const contentTop = frame.y + chromeHeight + 13;
  const contentBottom = frame.bottom - footerHeight - 7;
  const capacity = Math.max(1, Math.floor((contentBottom - contentTop) / lineHeight));
  const startIndex = windowStart(spec, capacity);
  const visible = spec.lines.slice(startIndex, startIndex + capacity);
  const gutterWidth = frame.narrow ? 25 : 31;
  const fontSize = frame.narrow ? 7.8 : spec.viewport.width >= 600 ? 9.7 : 8.8;
  const textX = frame.x + gutterWidth + (frame.narrow ? 17 : 20);
  const horizontalBudget = charBudget(frame.x + frame.width - textX - 9, fontSize);
  let horizontalCrop = false;

  addWindowChrome(builder, frame, chromeHeight, spec.context?.tabLabel ?? 'terminal', spec.context?.workingDirectory ?? '~');
  builder.line('artifact.terminal.gutter-rule', 'artifact.gutter', 'construction', point(frame.x + gutterWidth, frame.y + chromeHeight), point(frame.x + gutterWidth, frame.bottom - footerHeight));
  visible.forEach((line, visibleIndex) => {
    const sourceIndex = startIndex + visibleIndex;
    const y = contentTop + visibleIndex * lineHeight;
    const semanticId = executionArtifactLineId(line.id);
    horizontalCrop ||= line.text.length > horizontalBudget;
    if (line.kind === 'command') builder.text(`${semanticId}.prompt`, semanticId, 'active', point(textX - (frame.narrow ? 12 : 15), y), '$', { size: fontSize + 0.6, mono: true, weight: 700 });
    renderEvidenceLine(builder, line, sourceIndex, semanticId, point(textX, y), box(frame.x + 1, y - fontSize - 5, frame.width - 2, lineHeight), fontSize, frame.x + gutterWidth - 6);
  });
  return { startIndex, endIndex: startIndex + visible.length, horizontalCrop, subtitle: 'Terminal evidence · prompt, working directory and exit status remain instrument metadata.' };
}

function renderEditor(builder: SceneBuilder, spec: ExecutionArtifactSpec, frame: Frame): RenderResult {
  if (spec.context?.outputOpen !== undefined) return renderCodeRunner(builder, spec, frame);
  const source = spec.source!;
  const theme = spec.context?.theme ?? 'light';
  const dark = theme === 'dark';
  const canvasRole: ShapeRole = dark ? 'ide-dark-canvas' : 'ide-light-canvas';
  const chromeRole: ShapeRole = dark ? 'ide-dark-chrome' : 'ide-light-chrome';
  const gutterRole: ShapeRole = dark ? 'ide-dark-gutter' : 'ide-light-gutter';
  const textRole: ShapeRole = dark ? 'ide-dark-text' : 'ide-light-text';
  const mutedRole: ShapeRole = dark ? 'ide-dark-muted' : 'ide-light-muted';
  const terminalRole: ShapeRole = dark ? 'ide-dark-terminal' : 'ide-light-terminal';
  const selectionRole: ShapeRole = dark ? 'ide-dark-selection' : 'ide-light-selection';
  const titleHeight = 26;
  const tabHeight = 29;
  const breadcrumbHeight = 20;
  const statusHeight = 20;
  const activityWidth = frame.narrow ? 28 : 34;
  const gutterWidth = frame.narrow ? 29 : 35;
  const editorHeight = frame.narrow ? 165 : 158;
  const panelHeaderHeight = 25;
  const top = frame.y;
  const titleBottom = top + titleHeight;
  const tabBottom = titleBottom + tabHeight;
  const editorTop = tabBottom + breadcrumbHeight;
  const splitterY = editorTop + editorHeight;
  const statusY = frame.bottom - statusHeight;
  const outputTop = splitterY + panelHeaderHeight + 11;
  const outputBottom = statusY - 7;
  const outputLineHeight = frame.narrow ? 15.5 : 12.5;
  const outputFont = frame.narrow ? 7.1 : spec.viewport.width >= 600 ? 7.4 : 6.5;
  const outputCapacity = Math.max(1, Math.floor((outputBottom - outputTop) / outputLineHeight));
  const outputStart = lineWindowStart(spec.lines.length, outputCapacity, focusedIndex(spec));
  const visibleOutput = spec.lines.slice(outputStart, outputStart + outputCapacity);
  const outputX = frame.x + activityWidth + 11;
  const outputBudget = charBudget(frame.x + frame.width - outputX - 9, outputFont);
  let horizontalCrop = false;

  // A real workspace hierarchy: window title, activity rail, file tab,
  // breadcrumbs, numbered source editor, output terminal, then status bar.
  builder.rect('artifact.editor.window', 'artifact', canvasRole, box(frame.x, frame.y, frame.width, frame.height), { radius: 5, label: `${theme} code workspace` });
  builder.rect('artifact.editor.titlebar', 'artifact.chrome', chromeRole, box(frame.x, top, frame.width, titleHeight), { radius: 5 });
  builder.text('artifact.editor.app-title', 'artifact.chrome', mutedRole, point(frame.x + frame.width / 2, top + 17), `kiln-labs — ${source.fileName}`, { size: 6.8, mono: false, weight: 600, align: 'middle' });
  builder.text('artifact.editor.window-controls', 'artifact.chrome', mutedRole, point(frame.x + frame.width - 10, top + 17), '—  □  ×', { size: 7, mono: true, align: 'end' });

  builder.rect('artifact.editor.activitybar', 'artifact.chrome', gutterRole, box(frame.x, titleBottom, activityWidth, statusY - titleBottom));
  ['▧', '⌕', '⑂', '▷', '□'].forEach((icon, index) => builder.text(`artifact.editor.activity.${index}`, 'artifact.chrome', index === 0 ? 'syntax-accent' : mutedRole, point(frame.x + activityWidth / 2, titleBottom + 25 + index * 28), icon, { size: index === 3 ? 10 : 9, mono: true, weight: index === 0 ? 700 : 500, align: 'middle' }));
  builder.rect('artifact.editor.activity-active', 'artifact.chrome', 'ide-run', box(frame.x, titleBottom + 8, 2.5, 24));

  const workX = frame.x + activityWidth;
  const workWidth = frame.width - activityWidth;
  builder.rect('artifact.editor.tabbar', 'artifact.chrome', chromeRole, box(workX, titleBottom, workWidth, tabHeight));
  const tabWidth = Math.min(frame.narrow ? 148 : 184, workWidth - 64);
  builder.rect('artifact.editor.active-tab', 'artifact.chrome', canvasRole, box(workX, titleBottom, tabWidth, tabHeight));
  builder.text('artifact.editor.file-icon', 'artifact.chrome', 'syntax-accent', point(workX + 10, titleBottom + 19), 'SQL', { size: 6.2, mono: true, weight: 700 });
  builder.text('artifact.editor.tab', 'artifact.chrome', textRole, point(workX + 36, titleBottom + 19), source.fileName, { size: 7.5, mono: false, weight: 600 });
  builder.text('artifact.editor.tab-close', 'artifact.chrome', mutedRole, point(workX + tabWidth - 10, titleBottom + 19), '×', { size: 8, mono: true, align: 'end' });
  builder.rect('artifact.editor.run-button', 'artifact.chrome', 'ide-run', box(frame.x + frame.width - 48, titleBottom + 5, 39, 19), { radius: 3 });
  builder.text('artifact.editor.run', 'artifact.chrome', 'ide-inverse-text', point(frame.x + frame.width - 28.5, titleBottom + 18), '▶ RUN', { size: 6.2, mono: true, weight: 700, align: 'middle' });

  builder.rect('artifact.editor.breadcrumbs', 'artifact.chrome', canvasRole, box(workX, tabBottom, workWidth, breadcrumbHeight));
  builder.text('artifact.editor.breadcrumb', 'artifact.chrome', mutedRole, point(workX + 10, tabBottom + 14), `lessons  ›  sql  ›  ${source.fileName}`, { size: 6.2, mono: false, weight: 500 });
  builder.rect('artifact.editor.gutter', 'artifact.gutter', gutterRole, box(workX, editorTop, gutterWidth, editorHeight));
  builder.rect('artifact.editor.source', 'artifact.source', canvasRole, box(workX + gutterWidth, editorTop, workWidth - gutterWidth, editorHeight));

  const sourceLineHeight = frame.narrow ? 19 : 18.5;
  const sourceFont = frame.narrow ? 8.2 : spec.viewport.width >= 600 ? 9 : 8.1;
  const sourceStartY = editorTop + 21;
  source.lines.forEach((line, index) => {
    const number = index + 1;
    const y = sourceStartY + index * sourceLineHeight;
    const semanticId = executionArtifactSourceLineId(line.id);
    const rowBounds = box(workX, y - sourceFont - 5, workWidth, sourceLineHeight);
    if (number === source.activeLine) {
      builder.rect(`${semanticId}.active-band`, semanticId, selectionRole, box(workX, rowBounds.y, workWidth, rowBounds.height));
      builder.rect(`${semanticId}.active-rule`, semanticId, 'ide-run', box(workX, rowBounds.y, 2.5, rowBounds.height));
    }
    builder.text(`${semanticId}.number`, semanticId, number === source.activeLine ? 'syntax-accent' : mutedRole, point(workX + gutterWidth - 8, y), String(number), { size: sourceFont - 0.7, mono: true, weight: number === source.activeLine ? 700 : 500, align: 'end' });
    renderSourceTokens(builder, line.text, semanticId, point(workX + gutterWidth + 10, y), sourceFont, dark);
    builder.anchor(`${semanticId}.baseline`, semanticId, point(workX + gutterWidth + 10, y), 'right');
    builder.anchor(`${semanticId}.gutter`, semanticId, point(workX, y), 'left');
    builder.hit(semanticId, rowBounds);
  });

  // A few quiet minimap ticks make this read as an editor at a glance without
  // copying any one IDE's chrome.
  const minimapX = frame.x + frame.width - 10;
  source.lines.forEach((line, index) => builder.line(`artifact.editor.minimap.${index}`, 'artifact.source', index === (source.activeLine ?? 0) - 1 ? 'syntax-keyword' : mutedRole, point(minimapX - Math.min(20, line.text.length * .18), editorTop + 12 + index * 6), point(minimapX, editorTop + 12 + index * 6)));

  builder.rect('artifact.editor.terminal', 'artifact.output', terminalRole, box(workX, splitterY, workWidth, statusY - splitterY));
  builder.rect('artifact.editor.output-tabs', 'artifact.output', chromeRole, box(workX, splitterY, workWidth, panelHeaderHeight));
  builder.text('artifact.editor.output-tab', 'artifact.output', textRole, point(workX + 11, splitterY + 17), 'PROBLEMS   OUTPUT   DEBUG CONSOLE   TERMINAL', { size: frame.narrow ? 5.6 : 6.4, mono: false, weight: 700 });
  builder.line('artifact.editor.output-tab-active', 'artifact.output', 'ide-run', point(workX + (frame.narrow ? 125 : 145), splitterY + 23), point(workX + (frame.narrow ? 174 : 202), splitterY + 23));
  builder.text('artifact.editor.output-actions', 'artifact.output', mutedRole, point(frame.x + frame.width - 10, splitterY + 17), '⌃  □  ×', { size: 6.5, mono: true, align: 'end' });
  visibleOutput.forEach((line, visibleIndex) => {
    const sourceIndex = outputStart + visibleIndex;
    const y = outputTop + visibleIndex * outputLineHeight;
    const semanticId = executionArtifactLineId(line.id);
    horizontalCrop ||= line.text.length > outputBudget;
    renderIdeOutputLine(builder, line, sourceIndex, semanticId, point(outputX, y), box(workX + 1, y - outputFont - 4, workWidth - 2, outputLineHeight), outputFont, dark);
  });

  builder.rect('artifact.editor.statusbar', 'artifact.status', 'ide-status', box(frame.x, statusY, frame.width, statusHeight));
  builder.text('artifact.editor.status-left', 'artifact.status', 'ide-inverse-text', point(frame.x + 9, statusY + 14), `⑂ main   ✓ ${exitLabel(spec)}   0 errors`, { size: 5.9, mono: false, weight: 600 });
  builder.text('artifact.editor.output-status', 'artifact.status', 'ide-inverse-text', point(frame.x + frame.width - 9, statusY + 14), `${source.language.toUpperCase()}   Ln ${source.activeLine ?? 1}, Col 1   UTF-8`, { size: 5.9, mono: false, weight: 600, align: 'end' });

  return { startIndex: outputStart, endIndex: outputStart + visibleOutput.length, horizontalCrop, subtitle: `${theme === 'dark' ? 'Dark' : 'Light'} editor · numbered source above a verbatim execution terminal.` };
}

function renderCodeRunner(builder: SceneBuilder, spec: ExecutionArtifactSpec, frame: Frame): RenderResult {
  const source = spec.source!;
  const open = spec.context?.outputOpen === true;
  const dark = spec.context?.theme === 'dark';
  const gutterRole: ShapeRole = dark ? 'ide-dark-gutter' : 'ide-light-gutter';
  const mutedRole: ShapeRole = dark ? 'ide-dark-muted' : 'ide-light-muted';
  const selectionRole: ShapeRole = dark ? 'ide-dark-selection' : 'ide-light-selection';
  const sourceTop = frame.y + 10;
  const sourceFont = frame.narrow ? 9.2 : 10.6;
  const lineHeight = frame.narrow ? 22 : 24;
  const gutterWidth = frame.narrow ? 34 : 42;
  const textX = frame.x + gutterWidth + 13;

  builder.rect('artifact.runner.gutter', 'artifact.source', gutterRole, box(frame.x, sourceTop, gutterWidth, frame.bottom - sourceTop));
  const sourceStartY = sourceTop + (frame.narrow ? 28 : 31);
  source.lines.forEach((line, index) => {
    const number = index + 1;
    const y = sourceStartY + index * lineHeight;
    const semanticId = executionArtifactSourceLineId(line.id);
    const bounds = box(frame.x, y - sourceFont - 6, frame.width, lineHeight);
    if (number === source.activeLine) {
      builder.rect(`${semanticId}.active-band`, semanticId, selectionRole, bounds);
      builder.rect(`${semanticId}.active-rule`, semanticId, 'ide-run', box(frame.x, bounds.y, 3, bounds.height));
    }
    builder.text(`${semanticId}.number`, semanticId, number === source.activeLine ? 'syntax-accent' : mutedRole, point(frame.x + gutterWidth - 10, y), String(number), { size: sourceFont - 1, mono: true, weight: number === source.activeLine ? 700 : 500, align: 'end' });
    renderSourceTokens(builder, line.text, semanticId, point(textX, y), sourceFont, dark);
    builder.anchor(`${semanticId}.baseline`, semanticId, point(textX, y), 'right');
    builder.anchor(`${semanticId}.gutter`, semanticId, point(frame.x, y), 'left');
    builder.hit(semanticId, bounds);
  });

  if (!open) {
    return { startIndex: 0, endIndex: spec.lines.length, horizontalCrop: false, subtitle: 'Numbered source · output remains absent until the learner runs it.' };
  }

  const sheetY = frame.narrow ? frame.y + 190 : frame.y + 165;
  const sheetHeight = frame.bottom - sheetY;
  const sheetHeader = frame.narrow ? 44 : 46;
  const sheetFooter = 26;
  const outputFont = frame.narrow ? 7.1 : 8.2;
  const outputLineHeight = frame.narrow ? 15.5 : 16.8;
  const outputTop = sheetY + sheetHeader + 9;
  const outputBottom = frame.bottom - sheetFooter - 6;
  const capacity = Math.max(1, Math.floor((outputBottom - outputTop) / outputLineHeight));
  const outputStart = lineWindowStart(spec.lines.length, capacity, focusedIndex(spec));
  const visible = spec.lines.slice(outputStart, outputStart + capacity);
  const outputX = frame.x + 15;
  const horizontalBudget = charBudget(frame.width - 30, outputFont);
  let horizontalCrop = false;

  builder.rect('artifact.runner.sheet', 'artifact.output', 'ide-dark-terminal', box(frame.x, sheetY, frame.width, sheetHeight), { radius: 12, label: 'Raw output sheet' });
  builder.line('artifact.runner.sheet.handle', 'artifact.output', 'ide-dark-muted', point(frame.x + frame.width / 2 - 17, sheetY + 10), point(frame.x + frame.width / 2 + 17, sheetY + 10));
  builder.text('artifact.runner.sheet.title', 'artifact.output', 'ide-dark-text', point(frame.x + 15, sheetY + 31), 'Raw output', { size: frame.narrow ? 9 : 10, mono: false, weight: 700 });
  visible.forEach((line, visibleIndex) => {
    const sourceIndex = outputStart + visibleIndex;
    const y = outputTop + visibleIndex * outputLineHeight;
    const semanticId = executionArtifactLineId(line.id);
    horizontalCrop ||= line.text.length > horizontalBudget;
    renderIdeOutputLine(builder, line, sourceIndex, semanticId, point(outputX, y), box(frame.x + 2, y - outputFont - 4, frame.width - 4, outputLineHeight), outputFont, true);
  });
  builder.line('artifact.runner.sheet.footer-rule', 'artifact.output', 'ide-dark-muted', point(frame.x, frame.bottom - sheetFooter), point(frame.x + frame.width, frame.bottom - sheetFooter));
  builder.text('artifact.runner.sheet.footer-left', 'artifact.output', 'terminal-success', point(frame.x + 15, frame.bottom - 9), `${exitLabel(spec)} · PostgreSQL 16`, { size: frame.narrow ? 6.8 : 7.6, mono: true, weight: 600 });
  builder.text('artifact.runner.sheet.footer-right', 'artifact.crop-policy', 'ide-dark-muted', point(frame.x + frame.width - 15, frame.bottom - 9), runnerFooterLabel(spec.lines.length, horizontalCrop, frame.narrow), { size: frame.narrow ? 6.5 : 7.3, mono: true, weight: 600, align: 'end' });
  return { startIndex: outputStart, endIndex: outputStart + visible.length, horizontalCrop, subtitle: 'Numbered source · raw execution output summoned from the bottom.' };
}

function renderQueryInspector(builder: SceneBuilder, spec: ExecutionArtifactSpec, frame: Frame): RenderResult {
  const chromeHeight = 43;
  const summaryHeight = frame.narrow ? 47 : 53;
  const footerHeight = 36;
  const contentTop = frame.y + chromeHeight + summaryHeight + 11;
  const contentBottom = frame.bottom - footerHeight - 5;
  const lineHeight = frame.narrow ? 18 : 19.5;
  const capacity = Math.max(1, Math.floor((contentBottom - contentTop) / lineHeight));
  const startIndex = windowStart(spec, capacity);
  const visible = spec.lines.slice(startIndex, startIndex + capacity);
  const fontSize = frame.narrow ? 6.75 : spec.viewport.width >= 600 ? 8.25 : 7.5;
  const planX = frame.x + (frame.narrow ? 35 : 44);
  const horizontalBudget = charBudget(frame.x + frame.width - planX - 10, fontSize);
  let horizontalCrop = false;

  addInspectorChrome(builder, spec, frame, chromeHeight);
  const summaryY = frame.y + chromeHeight;
  builder.line('artifact.inspector.summary-bottom', 'artifact.chrome', 'structure', point(frame.x, summaryY + summaryHeight), point(frame.x + frame.width, summaryY + summaryHeight));
  const metrics = inspectorMetrics(spec);
  metrics.forEach((metric, index) => {
    const metricWidth = frame.width / metrics.length;
    const x = frame.x + index * metricWidth;
    if (index > 0) builder.line(`artifact.inspector.metric.${metric.id}.rule`, 'artifact.chrome', 'construction', point(x, summaryY + 7), point(x, summaryY + summaryHeight - 7));
    builder.text(`artifact.inspector.metric.${metric.id}.label`, 'artifact.chrome', 'annotation', point(x + metricWidth / 2, summaryY + 17), metric.label, { size: 6.7, mono: true, weight: 700, align: 'middle', uppercase: true });
    builder.text(`artifact.inspector.metric.${metric.id}.value`, metric.semanticId, metric.role, point(x + metricWidth / 2, summaryY + 37), metric.value, { size: frame.narrow ? 9 : 11, mono: true, weight: 700, align: 'middle' });
  });
  builder.line('artifact.inspector.tree-rail', 'artifact.gutter', 'construction', point(frame.x + 22, contentTop - 8), point(frame.x + 22, frame.bottom - footerHeight));
  visible.forEach((line, visibleIndex) => {
    const sourceIndex = startIndex + visibleIndex;
    const y = contentTop + visibleIndex * lineHeight;
    const semanticId = executionArtifactLineId(line.id);
    const depth = Math.min(3, Math.floor((line.text.length - line.text.trimStart().length) / 2));
    const x = planX + depth * (frame.narrow ? 5 : 8);
    horizontalCrop ||= line.text.length > horizontalBudget;
    if (line.kind === 'output' && line.text.trimStart().startsWith('->')) {
      builder.line(`${semanticId}.branch`, semanticId, 'construction', point(frame.x + 22, y - 4), point(x - 5, y - 4));
      builder.text(`${semanticId}.node`, semanticId, 'event', point(frame.x + 13, y), '◆', { size: 6, mono: true, align: 'middle' });
    } else if (line.kind === 'evidence') builder.text(`${semanticId}.badge`, semanticId, 'evidence', point(frame.x + 13, y), '●', { size: 6, mono: true, align: 'middle' });
    renderEvidenceText(builder, line, sourceIndex, semanticId, point(x, y), box(frame.x + 1, y - fontSize - 5, frame.width - 2, lineHeight), fontSize);
  });
  return { startIndex, endIndex: startIndex + visible.length, horizontalCrop, subtitle: 'Query inspector · structured plan regions retain the captured source line beneath every reading.' };
}

function renderProfiler(builder: SceneBuilder, spec: ExecutionArtifactSpec, frame: Frame): RenderResult {
  const chromeHeight = 42;
  const summaryHeight = 45;
  const footerHeight = 36;
  const contentTop = frame.y + chromeHeight + summaryHeight + 11;
  const contentBottom = frame.bottom - footerHeight - 5;
  const lineHeight = frame.narrow ? 17.5 : 19;
  const capacity = Math.max(1, Math.floor((contentBottom - contentTop) / lineHeight));
  const startIndex = windowStart(spec, capacity);
  const visible = spec.lines.slice(startIndex, startIndex + capacity);
  const fontSize = frame.narrow ? 6.5 : spec.viewport.width >= 600 ? 8 : 7.25;
  const textX = frame.x + (frame.narrow ? 31 : 39);
  const horizontalBudget = charBudget(frame.x + frame.width - textX - 8, fontSize);
  let horizontalCrop = false;

  addWindowChrome(builder, frame, chromeHeight, spec.context?.tabLabel ?? 'CPU profile', spec.context?.workingDirectory ?? './');
  const summaryY = frame.y + chromeHeight;
  builder.text('artifact.profiler.summary-title', 'artifact.chrome', 'structure', point(frame.x + 12, summaryY + 16), 'TOP · FLAT CPU', { size: 7.5, mono: true, weight: 700, uppercase: true });
  builder.text('artifact.profiler.summary-value', 'artifact.chrome', 'evidence', point(frame.x + frame.width - 12, summaryY + 16), '93.63% ACCOUNTED', { size: 8, mono: true, weight: 700, align: 'end' });
  builder.line('artifact.profiler.summary-rule', 'artifact.chrome', 'structure', point(frame.x, summaryY + summaryHeight), point(frame.x + frame.width, summaryY + summaryHeight));
  visible.forEach((line, visibleIndex) => {
    const sourceIndex = startIndex + visibleIndex;
    const y = contentTop + visibleIndex * lineHeight;
    const semanticId = executionArtifactLineId(line.id);
    const percent = flatPercent(line.text);
    horizontalCrop ||= line.text.length > horizontalBudget;
    if (percent !== undefined) {
      const barStart = frame.x + 5;
      const barEnd = barStart + Math.max(2, (frame.width - 12) * (percent / 100));
      builder.line(`${semanticId}.heat`, semanticId, line.kind === 'evidence' ? 'evidence' : 'series', point(barStart, y + 4), point(barEnd, y + 4));
    }
    renderEvidenceText(builder, line, sourceIndex, semanticId, point(textX, y), box(frame.x + 1, y - fontSize - 4, frame.width - 2, lineHeight), fontSize);
  });
  return { startIndex, endIndex: startIndex + visible.length, horizontalCrop, subtitle: 'Profiler evidence · measured samples stay verbatim while exact bars expose relative heat.' };
}

function addWindowChrome(builder: SceneBuilder, frame: Frame, height: number, tab: string, context: string): void {
  builder.rect('artifact.chrome', 'artifact.chrome', 'header', box(frame.x, frame.y, frame.width, height), { radius: frame.narrow ? 7 : 9 });
  [0, 1, 2].forEach((index) => builder.rect(`artifact.chrome.control.${index}`, 'artifact.chrome', index === 0 ? 'active' : index === 1 ? 'conditional' : 'evidence', box(frame.x + 12 + index * 13, frame.y + 14, 6.5, 6.5), { radius: 4 }));
  builder.text('artifact.chrome.tab', 'artifact.chrome', 'structure', point(frame.x + 57, frame.y + 24), fitTitle(tab, frame.narrow ? 18 : 30), { size: 8.2, mono: true, weight: 700 });
  builder.text('artifact.chrome.context', 'artifact.chrome', 'annotation', point(frame.x + frame.width - 11, frame.y + 24), fitTitle(context, frame.narrow ? 14 : 30), { size: 7.2, mono: true, weight: 600, align: 'end' });
}

function addInspectorChrome(builder: SceneBuilder, spec: ExecutionArtifactSpec, frame: Frame, height: number): void {
  builder.rect('artifact.chrome', 'artifact.chrome', 'header', box(frame.x, frame.y, frame.width, height), { radius: frame.narrow ? 7 : 9 });
  builder.text('artifact.inspector.database', 'artifact.chrome', 'active', point(frame.x + 13, frame.y + 27), '◉', { size: 10, mono: true, weight: 700 });
  builder.text('artifact.inspector.title', 'artifact.chrome', 'structure', point(frame.x + 32, frame.y + 27), fitTitle(spec.context?.tabLabel ?? 'Execution plan', frame.narrow ? 21 : 34), { size: 8.5, mono: true, weight: 700 });
  builder.text('artifact.inspector.tabs', 'artifact.chrome', 'annotation', point(frame.x + frame.width - 12, frame.y + 27), frame.narrow ? 'PLAN · RAW' : 'PLAN  METRICS  RAW', { size: 7, mono: true, weight: 700, align: 'end' });
}

function renderSourceTokens(builder: SceneBuilder, text: string, semanticId: string, at: ReturnType<typeof point>, fontSize: number, dark: boolean): void {
  builder.text(`${semanticId}.text`, semanticId, dark ? 'ide-dark-text' : 'ide-light-text', at, text, { size: fontSize, mono: true, weight: 500 });
}

function renderIdeOutputLine(builder: SceneBuilder, line: ArtifactLine, sourceIndex: number, semanticId: string, at: ReturnType<typeof point>, bounds: ReturnType<typeof box>, fontSize: number, dark: boolean): void {
  if (line.kind === 'evidence') {
    const x2 = bounds.x + bounds.width;
    const y2 = bounds.y + bounds.height;
    builder.path(`${semanticId}.highlight`, semanticId, dark ? 'ide-dark-selection' : 'ide-light-selection', `M ${bounds.x} ${bounds.y} L ${x2} ${bounds.y} L ${x2} ${y2} L ${bounds.x} ${y2} Z`, { label: `evidence output line ${sourceIndex + 1}: ${line.text}` });
  }
  if (line.kind === 'command') builder.text(`${semanticId}.prompt`, semanticId, 'terminal-success', point(at.x, at.y), '❯', { size: fontSize, mono: true, weight: 700 });
  const x = line.kind === 'command' ? at.x + fontSize * 2.2 : at.x;
  const role: ShapeRole = line.kind === 'muted'
    ? dark ? 'ide-dark-muted' : 'ide-light-muted'
    : line.kind === 'evidence'
      ? dark ? 'syntax-keyword-dark' : 'syntax-keyword'
      : dark ? 'ide-dark-text' : 'ide-light-text';
  builder.text(`${semanticId}.text`, semanticId, role, point(x, at.y), line.text, { size: fontSize, mono: true, weight: line.kind === 'evidence' ? 600 : 500 });
  builder.anchor(`${semanticId}.baseline`, semanticId, point(x, at.y), 'right');
  builder.anchor(`${semanticId}.gutter`, semanticId, point(bounds.x, at.y), 'left');
  builder.hit(semanticId, bounds);
}

function renderEvidenceLine(builder: SceneBuilder, line: ArtifactLine, sourceIndex: number, semanticId: string, at: ReturnType<typeof point>, bounds: ReturnType<typeof box>, fontSize: number, numberX: number): void {
  builder.text(`${semanticId}.number`, semanticId, 'annotation', point(numberX, at.y), String(sourceIndex + 1).padStart(2, '0'), { size: Math.max(6.2, fontSize - 1), mono: true, weight: 600, align: 'end' });
  renderEvidenceText(builder, line, sourceIndex, semanticId, at, bounds, fontSize);
}

function renderEvidenceText(builder: SceneBuilder, line: ArtifactLine, sourceIndex: number, semanticId: string, at: ReturnType<typeof point>, bounds: ReturnType<typeof box>, fontSize: number): void {
  // Evidence internals deliberately avoid rect/polygon geometry: those are the
  // only primitives the global teacher hand perturbs. The exact path/line
  // register keeps captured material instrument-authentic under every hand.
  if (line.kind === 'command' || line.kind === 'evidence') {
    const x2 = bounds.x + bounds.width;
    const y2 = bounds.y + bounds.height;
    builder.path(`${semanticId}.highlight`, semanticId, line.kind === 'evidence' ? 'evidence' : 'surface', `M ${bounds.x} ${bounds.y} L ${x2} ${bounds.y} L ${x2} ${y2} L ${bounds.x} ${y2} Z`, { label: `${line.kind}, source line ${sourceIndex + 1}: ${line.text}` });
  }
  builder.line(`${semanticId}.rail`, semanticId, railRoleForLine(line.kind), point(bounds.x + 1.5, bounds.y), point(bounds.x + 1.5, bounds.y + bounds.height));
  builder.text(`${semanticId}.text`, semanticId, roleForLine(line.kind), at, line.text, { size: fontSize, mono: true, weight: line.kind === 'command' || line.kind === 'evidence' ? 700 : 500 });
  builder.anchor(`${semanticId}.baseline`, semanticId, at, 'right');
  builder.anchor(`${semanticId}.gutter`, semanticId, point(bounds.x, at.y), 'left');
  builder.hit(semanticId, bounds);
}

function addSourceRelations(builder: SceneBuilder, spec: ExecutionArtifactSpec): void {
  spec.lines.forEach((line, index) => {
    const semanticId = executionArtifactLineId(line.id);
    builder.relation(`artifact.contains.${line.id}`, 'contains-verbatim-line', 'artifact', semanticId, `source line ${index + 1}`);
    if (index > 0) builder.relation(`artifact.follows.${line.id}`, 'follows-source-line', executionArtifactLineId(spec.lines[index - 1]!.id), semanticId, `${index} precedes ${index + 1}`);
    if (line.kind === 'evidence') builder.relation(`artifact.evidence.${line.id}`, 'supports-reading', semanticId, 'artifact', line.text);
  });
  spec.source?.lines.forEach((line, index) => {
    const semanticId = executionArtifactSourceLineId(line.id);
    builder.relation(`artifact.source.contains.${line.id}`, 'contains-source-line', 'artifact.source', semanticId, `${spec.source!.fileName}:${index + 1}`);
    if (index > 0) builder.relation(`artifact.source.follows.${line.id}`, 'follows-source-line', executionArtifactSourceLineId(spec.source!.lines[index - 1]!.id), semanticId, `${index} precedes ${index + 1}`);
  });
}

function addFooter(builder: SceneBuilder, spec: ExecutionArtifactSpec, surface: ArtifactSurface, frame: Frame, result: RenderResult, before: number, after: number): void {
  const footerHeight = 36;
  const footerY = frame.bottom - footerHeight;
  builder.rect('artifact.footer', 'artifact.footer', 'header', box(frame.x, footerY, frame.width, footerHeight), { radius: frame.narrow ? 7 : 9 });
  builder.text('artifact.footer.range', 'artifact.crop-policy', 'annotation', point(frame.x + 11, footerY + 15), visibleRangeLabel(result.startIndex, result.endIndex, spec.lines.length), { size: frame.narrow ? 6.7 : 7.6, mono: true, weight: 700, uppercase: true });
  builder.text('artifact.footer.policy', 'artifact.crop-policy', result.horizontalCrop ? 'conditional' : 'annotation', point(frame.x + 11, footerY + 28), cropPolicyLabel(before, after, result.horizontalCrop, frame.narrow), { size: frame.narrow ? 6.1 : 7, mono: true, weight: 700, uppercase: true });
  builder.text('artifact.footer.status', 'artifact.status', spec.context?.exitCode === 0 ? 'positive' : 'annotation', point(frame.x + frame.width - 11, footerY + 22), surface === 'query-inspector' ? 'ACTUAL' : exitLabel(spec), { size: 7.3, mono: true, weight: 700, align: 'end', uppercase: true });
}

function inspectorMetrics(spec: ExecutionArtifactSpec): Array<{ id: string; label: string; value: string; semanticId: string; role: ShapeRole }> {
  const rows = spec.lines.find((line) => line.id === 'hash-join')?.text.match(/actual time=[^ ]+ rows=(\d+)/)?.[1] ?? '—';
  const buffers = spec.lines.find((line) => line.id === 'join-buffers')?.text.match(/hit=(\d+)/)?.[1] ?? '—';
  const runtime = spec.lines.find((line) => line.id === 'execution-time')?.text.match(/([0-9.]+ ms)$/)?.[1] ?? '—';
  return [
    { id: 'rows', label: 'actual rows', value: rows, semanticId: executionArtifactLineId('hash-join'), role: 'code' },
    { id: 'buffers', label: 'shared hits', value: buffers, semanticId: executionArtifactLineId('join-buffers'), role: 'evidence' },
    { id: 'runtime', label: 'execution', value: runtime, semanticId: executionArtifactLineId('execution-time'), role: 'active' },
  ];
}

function roleForLine(kind: ArtifactLine['kind']): ShapeRole {
  return kind === 'command' ? 'active' : kind === 'evidence' ? 'evidence' : kind === 'muted' ? 'annotation' : 'code';
}

function railRoleForLine(kind: ArtifactLine['kind']): ShapeRole {
  return kind === 'command' ? 'pointer' : kind === 'evidence' ? 'evidence' : kind === 'muted' ? 'annotation' : 'construction';
}

function focusedIndex(spec: ExecutionArtifactSpec): number {
  return spec.focusLineId ? spec.lines.findIndex((line) => executionArtifactLineId(line.id) === spec.focusLineId) : -1;
}

function windowStart(spec: ExecutionArtifactSpec, capacity: number): number {
  return lineWindowStart(spec.lines.length, capacity, focusedIndex(spec));
}

function lineWindowStart(lineCount: number, capacity: number, focused: number): number {
  if (lineCount <= capacity) return 0;
  if (focused < 0) return 0;
  return Math.max(0, Math.min(lineCount - capacity, focused - Math.floor(capacity / 2)));
}

function visibleRangeLabel(start: number, end: number, total: number): string { return `SOURCE ${start + 1}–${end} / ${total}`; }

function cropPolicyLabel(before: number, after: number, horizontal: boolean, narrow: boolean): string {
  const hidden = before + after;
  if (hidden > 0 && horizontal) return narrow ? `⋮ ${hidden} HIDDEN · ↔ CROP · SOURCE INTACT` : `⋮ ${hidden} LINES HIDDEN · ↔ CROP · SOURCE UNCHANGED`;
  if (hidden > 0) return narrow ? `⋮ ${hidden} HIDDEN · SOURCE INTACT` : `⋮ ${hidden} LINES OUTSIDE WINDOW · SOURCE UNCHANGED`;
  if (horizontal) return narrow ? '↔ CROP · SOURCE INTACT' : '↔ HORIZONTAL CROP · SOURCE UNCHANGED';
  return narrow ? 'COMPLETE · SOURCE INTACT' : 'COMPLETE VERBATIM SOURCE · NO CROP';
}

function runnerFooterLabel(lineCount: number, horizontal: boolean, narrow: boolean): string {
  if (horizontal) return narrow ? `${lineCount} lines · ↔ crop` : `${lineCount} lines · ↔ horizontal crop`;
  return narrow ? `${lineCount} lines · source intact` : `${lineCount} lines · source unchanged`;
}

function exitLabel(spec: ExecutionArtifactSpec): string { return spec.context?.exitCode === undefined ? 'CAPTURED' : `EXIT ${spec.context.exitCode}`; }

function flatPercent(value: string): number | undefined {
  const match = /^\s*[0-9.]+s\s+([0-9.]+)%/.exec(value);
  return match ? Number(match[1]) : undefined;
}

function charBudget(pixelWidth: number, fontSize: number): number { return Math.max(1, Math.floor(pixelWidth / (fontSize * 0.61))); }

function fitTitle(value: string, max: number): string {
  if (value.length <= max) return value;
  return max <= 3 ? value.slice(0, max) : `${value.slice(0, max - 1)}…`;
}
